import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { BarChart3, TrendingUp, User, Calendar, Target, Eye, EyeOff, Filter, ChevronDown, ChevronUp } from 'lucide-react';

const IndividualTeamsLegacyChart2 = ({ selectedManager, finalData = [], h2hData = [], yearRange = [2012, 2024] }) => {
  const [chartData, setChartData] = useState([]);
  const [visibleManagers, setVisibleManagers] = useState(new Set());
  const [highlightedManager, setHighlightedManager] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showManagerControls, setShowManagerControls] = useState(false);

  const managers = ['Kevin', 'Jake', 'Lyons', 'Nipples', 'Dan', 'Shuds', 'Karp', 'Mills', 'Joey', 'Raffy', 'Oster', 'Scott', 'Sam'];
  
  // Manager colors matching other components
  const managerColors = {
    'Kevin': '#10b981', // Emerald
    'Jake': '#f59e0b', // Amber
    'Lyons': '#22d3ee', // Cyan
    'Nipples': '#06b6d4', // Sky
    'Dan': '#ef4444', // Red
    'Shuds': '#8b5cf6', // Violet
    'Karp': '#84cc16', // Lime
    'Mills': '#14b8a6', // Teal
    'Joey': '#eab308', // Yellow
    'Raffy': '#ec4899', // Pink
    'Oster': '#3b82f6', // Blue
    'Scott': '#f97316', // Orange
    'Sam': '#6366f1'   // Indigo
  };

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Toggle manager visibility
  const toggleManager = (manager) => {
    const newVisible = new Set(visibleManagers);
    if (newVisible.has(manager)) {
      newVisible.delete(manager);
    } else {
      newVisible.add(manager);
    }
    setVisibleManagers(newVisible);
  };

  // Show/hide all managers
  const toggleAllManagers = () => {
    if (visibleManagers.size === managers.length) {
      setVisibleManagers(new Set());
    } else {
      setVisibleManagers(new Set(managers));
    }
  };

  // Get bar color based on visibility and highlighting
  const getBarColor = (team) => {
    const isVisible = visibleManagers.has(team.manager);
    const isHighlighted = highlightedManager === team.manager;
    
    if (visibleManagers.size === 0) {
      // No filters - show score-based colors
      if (team.legacyScore >= 100) return '#10b981';
      else if (team.legacyScore >= 50) return '#84cc16';
      else if (team.legacyScore >= 0) return '#eab308';
      else if (team.legacyScore >= -50) return '#f97316';
      else return '#ef4444';
    } else {
      // Filters active
      if (isVisible) {
        return isHighlighted ? managerColors[team.manager] : managerColors[team.manager] + 'CC';
      } else {
        return '#e5e7eb'; // Light gray for hidden managers
      }
    }
  };

  // Calculate weekly positions exactly like other components
  const calculateWeeklyPositions = (regSeasonGames, manager = '') => {
    const weeklyPositions = {
      first: 0, twelfth: 0, second: 0, eleventh: 0, third: 0, tenth: 0,
      fourth: 0, ninth: 0, fifth: 0, eighth: 0, sixth: 0, seventh: 0
    };

    regSeasonGames.forEach(game => {
      const winsThisWeek = game['Wins (This Week)'] || 0;
      const lossesThisWeek = game['Losses (This Week)'] || 0;
      
      // Validate data integrity
      if (winsThisWeek + lossesThisWeek !== 11) {
        console.warn('Invalid weekly ranking data for', manager, ':', {
          year: game.Year,
          week: game.Week,
          manager: game.Manager1,
          wins: winsThisWeek,
          losses: lossesThisWeek,
          total: winsThisWeek + lossesThisWeek
        });
        return; // Skip invalid records
      }
      
      // Weekly position logic
      if (winsThisWeek === 11) weeklyPositions.first += 1;
      if (winsThisWeek === 0) weeklyPositions.twelfth += 1;
      if (winsThisWeek === 10) weeklyPositions.second += 1;
      if (winsThisWeek === 1) weeklyPositions.eleventh += 1;
      if (winsThisWeek === 9) weeklyPositions.third += 1;
      if (winsThisWeek === 2) weeklyPositions.tenth += 1;
      if (winsThisWeek === 8) weeklyPositions.fourth += 1;
      if (winsThisWeek === 3) weeklyPositions.ninth += 1;
      if (winsThisWeek === 7) weeklyPositions.fifth += 1;
      if (winsThisWeek === 4) weeklyPositions.eighth += 1;
      if (winsThisWeek === 6) weeklyPositions.sixth += 1;
      if (winsThisWeek === 5) weeklyPositions.seventh += 1;
    });

    return weeklyPositions;
  };

  // Calculate legacy score for a specific team (manager-year combination)
  const calculateTeamLegacyScore = (manager, year) => {
    if (!h2hData.length || !finalData.length) return 0;

    // Get season data for this manager-year
    const seasonData = finalData.find(season => 
      season.Owner === manager && season.Year === year
    );

    if (!seasonData) return 0;

    // Get H2H games for this manager-year
    const managerH2H = h2hData.filter(game => 
      game.Manager1 === manager && game.Year === year
    );

    // Regular season wins/losses
    const regSeasonGames = managerH2H.filter(game => game.Type === 'Regular Season');
    const regWins = regSeasonGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);
    const regLosses = regSeasonGames.reduce((sum, game) => sum + (game.IsLoss || 0), 0);

    // Weekly positions
    const weeklyPositions = calculateWeeklyPositions(regSeasonGames, manager);

    // Playoff wins
    const playoffGames = managerH2H.filter(game => game.Type === 'Playoffs');
    const playoffWins = playoffGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);

    // Standings score
    let standingsScore = 0;
    if (seasonData.Standings === 1) standingsScore = 125;
    else if (seasonData.Standings === 2) standingsScore = 75;
    else if (seasonData.Standings === 3) standingsScore = 60;
    else if (seasonData.Standings === 4) standingsScore = 40;
    else if (seasonData.Standings === 5) standingsScore = 30;
    else if (seasonData.Standings === 6) standingsScore = 15;
    else if (seasonData.Standings === 7) standingsScore = -15;
    else if (seasonData.Standings === 8) standingsScore = -30;
    else if (seasonData.Standings === 9) standingsScore = -40;
    else if (seasonData.Standings === 10) standingsScore = -50;
    else if (seasonData.Standings === 11) standingsScore = -60;
    else if (seasonData.Standings === 12) standingsScore = -80;

    // Calculate scoring champion/loser for this year
    const yearSeasons = finalData.filter(s => s.Year === year);
    let scoreChamp = 0;
    let scoreLoser = 0;

    if (yearSeasons.length > 0) {
      const maxPF = Math.max(...yearSeasons.map(s => s.PF || 0));
      const minPF = Math.min(...yearSeasons.map(s => s.PF || 0));
      
      if (seasonData.PF === maxPF) scoreChamp = 1;
      if (seasonData.PF === minPF) scoreLoser = 1;
    }

    // Calculate total legacy score
    const totalScore = regWins * 5 + regLosses * -5 +
                      weeklyPositions.first * 15 + weeklyPositions.twelfth * -15 +
                      weeklyPositions.second * 5 + weeklyPositions.eleventh * -5 +
                      weeklyPositions.third * 4 + weeklyPositions.tenth * -4 +
                      weeklyPositions.fourth * 3 + weeklyPositions.ninth * -3 +
                      weeklyPositions.fifth * 2 + weeklyPositions.eighth * -2 +
                      weeklyPositions.sixth * 1 + weeklyPositions.seventh * -1 +
                      playoffWins * 10 +
                      standingsScore +
                      scoreChamp * 30 + scoreLoser * -30;

    return Math.round(totalScore);
  };

  // Prepare chart data
  const prepareChartData = () => {
    if (!h2hData.length || !finalData.length) return [];

    const teamData = [];

    // Get all team data and calculate legacy scores
    finalData.forEach(season => {
      if (season.Year >= yearRange[0] && season.Year <= yearRange[1]) {
        const legacyScore = calculateTeamLegacyScore(season.Owner, season.Year);
        
        teamData.push({
          yearManager: `${season.Year} ${season.Owner}`,
          shortYearManager: `'${season.Year.toString().slice(-2)} ${season.Owner.slice(0, isMobile ? 3 : 5)}`,
          year: season.Year,
          manager: season.Owner,
          team: season.Team || `${season.Owner} ${season.Year}`,
          legacyScore: legacyScore,
          standings: season.Standings,
          wins: season.Wins,
          losses: season.Losses,
          pointsFor: season.PF,
          isSelectedManager: season.Owner === selectedManager
        });
      }
    });

    // Sort by legacy score descending
    return teamData.sort((a, b) => b.legacyScore - a.legacyScore);
  };

  // Custom tooltip with mobile optimization
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isVisible = visibleManagers.has(data.manager);
      
      return (
        <div className={`bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-2xl p-3 ${
          isMobile ? 'max-w-xs text-xs' : 'max-w-sm text-sm'
        }`}>
          <div className={`font-bold text-gray-900 mb-2 border-b border-gray-100 pb-2 flex items-center ${
            isMobile ? 'text-sm' : 'text-base'
          }`}>
            <div 
              className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
              style={{ backgroundColor: managerColors[data.manager] }}
            />
            <span className="truncate">{data.yearManager}</span>
            {isVisible && (
              <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full flex-shrink-0">
                Highlighted
              </span>
            )}
          </div>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-600">Manager:</span>
              <span className="font-medium">{data.manager}</span>
            </div>
            {!isMobile && (
              <div className="flex justify-between">
                <span className="text-gray-600">Team:</span>
                <span className="font-medium truncate ml-2">{data.team}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Legacy Score:</span>
              <span className={`font-bold ${
                data.legacyScore >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {data.legacyScore >= 0 ? '+' : ''}{data.legacyScore}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Record:</span>
              <span className="font-medium">{data.wins}-{data.losses}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Standings:</span>
              <span className="font-medium">
                {data.standings === 1 ? '1st' : 
                 data.standings === 2 ? '2nd' : 
                 data.standings === 3 ? '3rd' : 
                 `${data.standings}th`}
              </span>
            </div>
            {!isMobile && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Points For:</span>
                  <span className="font-medium">{data.pointsFor?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">All-Time Rank:</span>
                  <span className="font-medium">
                    #{chartData.findIndex(item => item.yearManager === data.yearManager) + 1} of {chartData.length}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    try {
      setLoading(true);
      setError(null);

      if (h2hData.length && finalData.length) {
        const data = prepareChartData();
        setChartData(data);
        
        // Initialize with no managers selected (show all by default)
        setVisibleManagers(new Set());
        
        // Debug output for verification
        if (data.length > 0) {
          console.log('=== INDIVIDUAL TEAMS LEGACY DEBUG ===');
          console.log('Total teams:', data.length);
          console.log('Top team:', data[0]);
          console.log('Bottom team:', data[data.length - 1]);
          
          if (selectedManager) {
            const selectedTeams = data.filter(t => t.manager === selectedManager);
            console.log(`${selectedManager} teams:`, selectedTeams.length);
          }
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error calculating individual teams legacy:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [h2hData, finalData, yearRange, selectedManager, isMobile]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-purple-600 mx-auto mb-3 sm:mb-4"></div>
        <p className="text-gray-600 text-sm sm:text-base">Calculating individual team legacy scores...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
        <div className="text-red-500 mb-4">
          <BarChart3 className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-2" />
          <p className="font-semibold text-sm sm:text-base">Error Loading Chart</p>
          <p className="text-xs sm:text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
        <div className="text-gray-500">
          <BarChart3 className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-4" />
          <p className="text-base sm:text-lg font-semibold">No Data Available</p>
          <p className="text-xs sm:text-sm">No team data found for the selected criteria.</p>
        </div>
      </div>
    );
  }

  const selectedTeams = chartData.filter(team => team.isSelectedManager);
  const bestTeam = chartData[0];
  const worstTeam = chartData[chartData.length - 1];

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6 sm:mb-8">
      {/* Header - Mobile Optimized */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 px-4 sm:px-6 py-3 sm:py-4">
        <div className={`flex items-center justify-between ${isMobile ? 'flex-col space-y-3' : ''}`}>
          <div className="flex items-center flex-1 min-w-0">
            <BarChart3 className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'} mr-2 sm:mr-3 text-white flex-shrink-0`} />
            <div className="min-w-0 flex-1">
              <h2 className={`font-bold text-white ${isMobile ? 'text-lg' : 'text-xl'}`}>
                {selectedManager ? `${selectedManager}'s ` : ''}
                {isMobile ? 'Individual Teams' : 'Legacy Score by Individual Teams'}
              </h2>
              <p className={`text-purple-100 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                {isMobile 
                  ? `Single-season scores (${yearRange[0]}-${yearRange[1]})`
                  : `Single-season legacy scores showing the greatest and worst teams (${yearRange[0]}-${yearRange[1]})`
                }
              </p>
            </div>
          </div>
          
          {/* Controls - Mobile Optimized */}
          <div className={`flex items-center space-x-3 ${isMobile ? 'w-full justify-between' : ''}`}>
            <button
              onClick={() => setShowManagerControls(!showManagerControls)}
              className={`flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg transition-colors duration-200 ${
                isMobile ? 'flex-1 justify-center' : ''
              }`}
            >
              <Filter className="h-4 w-4" />
              <span className="text-sm">
                {isMobile ? 'Filter' : 'Manager Filter'}
              </span>
              {isMobile && (
                showManagerControls ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
              )}
            </button>
            
            {!isMobile && (
              <button
                onClick={toggleAllManagers}
                className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg transition-colors duration-200"
              >
                {visibleManagers.size === managers.length ? (
                  <><EyeOff className="h-4 w-4" /><span className="text-sm">Hide All</span></>
                ) : (
                  <><Eye className="h-4 w-4" /><span className="text-sm">Show All</span></>
                )}
              </button>
            )}
            
            <div className={`text-purple-100 text-sm ${isMobile ? 'flex-shrink-0' : ''}`}>
              {visibleManagers.size}/{managers.length} highlighted
            </div>
          </div>
        </div>
      </div>

      {/* Manager Controls - Mobile Optimized with Toggle */}
      {(!isMobile || showManagerControls) && (
        <div className="p-3 sm:p-4 border-b border-gray-200 bg-gray-50">
          {/* Mobile Toggle All Button */}
          {isMobile && (
            <div className="mb-3 flex justify-center">
              <button
                onClick={toggleAllManagers}
                className="flex items-center space-x-2 bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-lg transition-colors duration-200"
              >
                {visibleManagers.size === managers.length ? (
                  <><EyeOff className="h-4 w-4" /><span className="text-sm">Hide All</span></>
                ) : (
                  <><Eye className="h-4 w-4" /><span className="text-sm">Show All</span></>
                )}
              </button>
            </div>
          )}
          
          {/* Manager Grid - Mobile Optimized */}
          <div className={`grid gap-2 sm:gap-3 ${
            isMobile ? 'grid-cols-2' : 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8'
          }`}>
            {managers.map(manager => {
              const isVisible = visibleManagers.has(manager);
              const isHighlighted = highlightedManager === manager;
              const managerTeams = chartData.filter(team => team.manager === manager);
              
              return (
                <button
                  key={manager}
                  onClick={() => toggleManager(manager)}
                  onMouseEnter={() => !isMobile && setHighlightedManager(manager)}
                  onMouseLeave={() => !isMobile && setHighlightedManager(null)}
                  className={`relative flex items-center space-x-2 sm:space-x-3 px-2 sm:px-4 py-2 sm:py-3 rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                    isVisible
                      ? 'border-transparent shadow-lg transform scale-105'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  } ${isHighlighted ? 'ring-2 ring-purple-300 ring-offset-2' : ''}`}
                  style={{
                    backgroundColor: isVisible ? managerColors[manager] + '20' : undefined,
                    borderColor: isVisible ? managerColors[manager] : undefined
                  }}
                >
                  {/* Manager Image Background - More Subtle on Mobile */}
                  <div className={`absolute inset-0 ${isMobile ? 'opacity-10' : 'opacity-20'}`}>
                    <img 
                      src={`/${manager.toLowerCase()}main.png`}
                      alt={`${manager} background`}
                      className="w-full h-full object-cover object-center"
                      onError={(e) => {
                        if (e.target.src.includes('main.png')) {
                          e.target.src = `/${manager.toLowerCase()}.png`;
                        } else {
                          e.target.style.display = 'none';
                        }
                      }}
                    />
                  </div>

                  {/* Manager Avatar - Mobile Optimized */}
                  <div className={`relative z-10 rounded-full overflow-hidden border-2 border-white shadow-md flex-shrink-0 ${
                    isMobile ? 'w-6 h-6' : 'w-8 h-8'
                  }`}>
                    <img 
                      src={`/${manager.toLowerCase()}main.png`}
                      alt={manager}
                      className="w-full h-full object-cover object-center"
                      onError={(e) => {
                        if (e.target.src.includes('main.png')) {
                          e.target.src = `/${manager.toLowerCase()}.png`;
                        } else {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<div class="w-full h-full rounded-full" style="background-color: ${managerColors[manager]}"></div>`;
                        }
                      }}
                    />
                  </div>

                  {/* Manager Name - Mobile Optimized */}
                  <span className={`relative z-10 font-medium truncate ${
                    isMobile ? 'text-xs' : 'text-sm'
                  } ${isVisible ? 'text-gray-900' : 'text-gray-600'}`}>
                    {isMobile ? manager.slice(0, 6) : manager}
                  </span>

                  {/* Team Count Badge - Mobile Optimized */}
                  {managerTeams.length > 0 && (
                    <div className={`relative z-10 bg-white/90 text-gray-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full shadow-sm flex-shrink-0 ${
                      isMobile ? 'text-xs' : 'text-xs'
                    }`}>
                      {managerTeams.length}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Filter Info - Mobile Optimized */}
          {visibleManagers.size > 0 && (
            <div className={`mt-3 text-gray-600 ${isMobile ? 'text-xs text-center' : 'text-sm'}`}>
              Highlighting teams from {visibleManagers.size} manager{visibleManagers.size > 1 ? 's' : ''}. 
              Other teams are shown in gray.
            </div>
          )}
        </div>
      )}

      {/* Chart - Mobile Optimized */}
      <div className="p-2 sm:p-4">
        <div style={{ width: '100%', height: isMobile ? '300px' : '350px' }}>
          <ResponsiveContainer>
            <BarChart 
              data={chartData} 
              margin={{ 
                top: 10, 
                right: isMobile ? 10 : 20, 
                left: isMobile ? 15 : 20, 
                bottom: 10 
              }}
              barCategoryGap={isMobile ? 0 : 1}
            >
              <CartesianGrid strokeDasharray="2 4" stroke="#f1f5f9" opacity={0.4} vertical={false} />
              <XAxis 
                dataKey={isMobile ? "shortYearManager" : "yearManager"}
                hide={true}
              />
              <YAxis 
                stroke="#64748b"
                fontSize={isMobile ? 10 : 11}
                fontWeight="500"
                tickFormatter={(value) => value >= 0 ? `+${value}` : `${value}`}
                axisLine={false}
                tickLine={false}
                width={isMobile ? 35 : 40}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Main bars with dynamic color assignment */}
              <Bar 
                dataKey="legacyScore"
                radius={[isMobile ? 1 : 2, isMobile ? 1 : 2, 0, 0]}
              >
                {chartData.map((team, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getBarColor(team)}
                    stroke={visibleManagers.has(team.manager) && highlightedManager === team.manager ? '#ffffff' : 'none'}
                    strokeWidth={2}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Stats Summary - Mobile Optimized */}
      {selectedManager && selectedTeams.length > 0 && (
        <div className="px-4 sm:px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center">
            <div>
              <div className={`font-bold text-gray-900 ${isMobile ? 'text-base' : 'text-lg'}`}>
                {selectedTeams.length}
              </div>
              <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>Total Teams</div>
            </div>
            <div>
              <div className={`font-bold text-green-600 ${isMobile ? 'text-base' : 'text-lg'}`}>
                {selectedTeams.filter(team => team.legacyScore > 0).length}
              </div>
              <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>Positive Scores</div>
            </div>
            <div>
              <div className={`font-bold text-purple-600 ${isMobile ? 'text-base' : 'text-lg'}`}>
                {Math.round(selectedTeams.reduce((sum, team) => sum + team.legacyScore, 0) / selectedTeams.length)}
              </div>
              <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>Average Score</div>
            </div>
          </div>
        </div>
      )}

      {/* Filtering Summary - Mobile Optimized */}
      {visibleManagers.size > 0 && (
        <div className="px-4 sm:px-6 py-3 bg-blue-50 border-t border-blue-200">
          <div className="grid grid-cols-2 gap-3 sm:gap-4 text-center">
            <div>
              <div className={`font-bold text-blue-600 ${isMobile ? 'text-base' : 'text-lg'}`}>
                {chartData.filter(team => visibleManagers.has(team.manager)).length}
              </div>
              <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>Highlighted Teams</div>
            </div>
            <div>
              <div className={`font-bold text-gray-600 ${isMobile ? 'text-base' : 'text-lg'}`}>
                {chartData.filter(team => !visibleManagers.has(team.manager)).length}
              </div>
              <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>Other Teams</div>
            </div>
          </div>
        </div>
      )}

      {/* Legend and Summary - Mobile Optimized */}
      <div className="p-3 sm:p-4 bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200">
        <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
          {/* Color Legend - Mobile Optimized */}
          <div>
            <h4 className={`font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center ${
              isMobile ? 'text-sm' : 'text-sm'
            }`}>
              <div className="w-1 h-4 bg-gradient-to-b from-purple-500 to-indigo-600 rounded-full mr-2"></div>
              {visibleManagers.size === 0 ? 'Legacy Score Scale' : 'Manager Highlighting'}
            </h4>
            {visibleManagers.size === 0 ? (
              <div className={`grid gap-1 sm:gap-2 ${
                isMobile ? 'grid-cols-1 text-xs' : 'grid-cols-2 text-xs'
              }`}>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-emerald-500 rounded mr-2 flex-shrink-0"></div>
                  <span>Exceptional (100+)</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-lime-500 rounded mr-2 flex-shrink-0"></div>
                  <span>Great (50-99)</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded mr-2 flex-shrink-0"></div>
                  <span>Good (0-49)</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-orange-500 rounded mr-2 flex-shrink-0"></div>
                  <span>Poor (-1 to -49)</span>
                </div>
                <div className={`flex items-center ${isMobile ? '' : 'col-span-2'}`}>
                  <div className="w-3 h-3 bg-red-500 rounded mr-2 flex-shrink-0"></div>
                  <span>Terrible (-50+)</span>
                </div>
              </div>
            ) : (
              <div className="space-y-1 sm:space-y-2 text-xs">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-purple-500 rounded mr-2 flex-shrink-0"></div>
                  <span>Highlighted managers' teams</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gray-300 rounded mr-2 flex-shrink-0"></div>
                  <span>Other managers' teams</span>
                </div>
                <div className="text-gray-600 text-xs mt-1 sm:mt-2">
                  {isMobile ? 'Toggle "Show All" to reset colors' : 'Click "Hide All" to return to score-based coloring'}
                </div>
              </div>
            )}
          </div>

          {/* All-Time Records - Mobile Optimized */}
          <div>
            <h4 className={`font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center ${
              isMobile ? 'text-sm' : 'text-sm'
            }`}>
              <div className="w-1 h-4 bg-gradient-to-b from-yellow-500 to-orange-500 rounded-full mr-2"></div>
              All-Time Records
            </h4>
            <div className="space-y-1 sm:space-y-2 text-xs">
              <div className="flex justify-between items-center bg-green-50 rounded p-2 border border-green-200">
                <span className="text-gray-700 font-medium flex-shrink-0">🏆 Best:</span>
                <span className="font-bold text-green-700 truncate ml-2">
                  {isMobile ? bestTeam.shortYearManager : bestTeam.yearManager} (+{bestTeam.legacyScore})
                </span>
              </div>
              <div className="flex justify-between items-center bg-red-50 rounded p-2 border border-red-200">
                <span className="text-gray-700 font-medium flex-shrink-0">💀 Worst:</span>
                <span className="font-bold text-red-700 truncate ml-2">
                  {isMobile ? worstTeam.shortYearManager : worstTeam.yearManager} ({worstTeam.legacyScore})
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndividualTeamsLegacyChart2;